import { ChartV2, ChartV2Handle } from '@chartv2';
import {
  terminalPanesToDefaultHiddenSeriesIds,
  terminalPanesToDefaultPriceDisplayByPane,
  terminalPanesToSeries,
  toChartV2Domain,
  toChartV2Horizon,
  toChartV2Interval,
  toRangeHorizon,
  toResampleInterval,
} from '@terminal/views/chart-v2/utils/map-terminal-chart';
import { useTurboChart } from '@terminal/views/turbo-chart/hooks/use-turbo-chart/useTurboChart';
import {
  checkSearchParams,
  getChartSettingsFromSearchParams,
  ValidChartSettings,
} from '@terminal/views/turbo-chart/utils/chart-settings-utils/chart-settings-utils';
import { SkeletonLoader } from '@toggle/design-system/src/components/skeleton-loader/SkeletonLoader';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useVisualizationStore } from '../use-visualization-store/useVisualizationStore';
import styles from './ChartV2MarkdownLink.module.scss';

export const CHARTV2_MARKDOWN_LINK_TITLE = 'line-chart';

export const isChartV2MarkdownTitle = (title: string | undefined): boolean =>
  title?.toLowerCase().includes(CHARTV2_MARKDOWN_LINK_TITLE) ?? false;

export const isChartV2MarkdownHref = (href: string | undefined): boolean => {
  if (!href) {
    return false;
  }

  try {
    const url = new URL(href, window.location.origin);

    return url.pathname === '/app/chartv2';
  } catch {
    return false;
  }
};

export const getChartV2MarkdownSettings = (
  href: string | undefined
): ValidChartSettings | null => {
  if (!href) {
    return null;
  }

  try {
    const url = new URL(href, window.location.origin);

    if (!isChartV2MarkdownHref(href)) {
      return null;
    }

    const settings = getChartSettingsFromSearchParams(url.searchParams);

    return checkSearchParams(settings) ? settings : null;
  } catch {
    return null;
  }
};

export interface ChartV2MarkdownLinkProps {
  href?: string;
}

export const ChartV2MarkdownLink = ({ href }: ChartV2MarkdownLinkProps) => {
  const chartV2Ref = useRef<ChartV2Handle>(null);
  const appliedDomainKeyRef = useRef<string | null>(null);
  const settings = useMemo(() => getChartV2MarkdownSettings(href), [href]);
  const setCharts = useVisualizationStore(state => state.setCharts);
  const registerChart = useCallback(
    (node: HTMLDivElement | null) => {
      if (node && href) {
        setCharts(href, node);
      }
    },
    [href, setCharts]
  );

  const {
    chartState,
    enabledResamples,
    disable1DHorizon,
    chartIsLoading,
    changeRange,
    changeResample,
  } = useTurboChart({
    initialChartSettings: settings ?? undefined,
    shouldUseSearchParams: false,
  });

  const { horizon, resample, chartPanes } = chartState;
  const primaryAsset = chartPanes[0]?.chartAssetData[0];

  const series = useMemo(() => terminalPanesToSeries(chartPanes), [chartPanes]);

  const domain = useMemo(
    () => toChartV2Domain(chartState.domain, primaryAsset),
    [chartState.domain, primaryAsset]
  );

  useEffect(() => {
    if (!domain || series.length === 0) {
      appliedDomainKeyRef.current = null;
      return;
    }

    const key = `${domain.start}:${domain.end}`;

    if (appliedDomainKeyRef.current === key || !chartV2Ref.current) {
      return;
    }

    chartV2Ref.current.setDomain(domain);
    appliedDomainKeyRef.current = key;
  }, [domain, series.length]);

  if (!settings) {
    return null;
  }

  if (chartIsLoading) {
    return (
      <div className={styles.root}>
        <SkeletonLoader
          areas={[
            {
              styles: {
                width: '100%',
                height: '100%',
              },
            },
          ]}
          className={styles.skeletonLoader}
        />
      </div>
    );
  }

  return (
    <div
      className={styles.root}
      data-testid="chartv2-markdown-link"
      ref={registerChart}
    >
      <ChartV2
        ref={chartV2Ref}
        className={styles.chart}
        series={series}
        horizon={toChartV2Horizon(horizon)}
        interval={toChartV2Interval(resample)}
        enabledIntervals={enabledResamples.map(toChartV2Interval)}
        disabledHorizons={disable1DHorizon ? ['1D'] : undefined}
        defaultHiddenSeriesIds={terminalPanesToDefaultHiddenSeriesIds(
          chartPanes
        )}
        defaultPriceDisplayByPane={terminalPanesToDefaultPriceDisplayByPane(
          chartPanes
        )}
        onHorizonChange={next => changeRange(toRangeHorizon(next))}
        onIntervalChange={next => changeResample(toResampleInterval(next))}
        showDefaultLegend={false}
      />
    </div>
  );
};
